import { INPUT_DATE_SELECT_FORMAT, InputSelectOption } from '../types/InputDateSelectTransformerTypes';
import { stringTwoDigitNumber } from './OptionsGetters';

export const FORMAT_KEY = {
	YEAR: 'y',
	MONTH: 'm',
	DAY: 'd'
} as const;
type FORMAT_KEY = (typeof FORMAT_KEY)[keyof typeof FORMAT_KEY];

/**
 * Retrieve the format to be used in the input depending of the locale of the user
 */
export const getLocaleFormat = (locale?: string): string[] => {
	const year = 2222;
	const month = 12;
	const day = 17;
	const date = new Date(year, month - 1, day);
	const formattedDateParts = new Intl.DateTimeFormat(locale).formatToParts(date);

	return formattedDateParts
		.filter((val) => val.type !== 'literal')
		.map((val) => {
			if (val.type === 'day') return FORMAT_KEY.DAY;
			if (val.type === 'month') return FORMAT_KEY.MONTH;
			return FORMAT_KEY.YEAR;
		});
};

/**
 * Get the order and availability of the select of the input Date Select
 */
export const getSelectOrder = ({
	locale,
	format
}: {
	locale?: string;
	format?: INPUT_DATE_SELECT_FORMAT;
}): FORMAT_KEY[] => {
	if (format === INPUT_DATE_SELECT_FORMAT.CREDIT_CARD) {
		return [FORMAT_KEY.MONTH, FORMAT_KEY.YEAR];
	}
	return getLocaleFormat(locale) as unknown as FORMAT_KEY[];
};

/**
 * Giving a date converts it to the format wanted for an output
 */
export function fromDateToFormat(date: Date, format: INPUT_DATE_SELECT_FORMAT): Date | string {
	if (format === INPUT_DATE_SELECT_FORMAT.DATE) return date;
	if (format === INPUT_DATE_SELECT_FORMAT.ISO_STRING) {
		const monthString = stringTwoDigitNumber(date.getMonth() + 1);
		const dateString = stringTwoDigitNumber(date.getDate());
		return `${date.getFullYear().toString()}-${monthString}-${dateString}`;
	}
	const lastTwoDigitsYear = date.getFullYear().toString().slice(-2);
	const monthString = stringTwoDigitNumber(date.getMonth() + 1);
	return `${monthString}${lastTwoDigitsYear}`;
}

/**
 * Giving a string or date with one of the formats, retrieve the date corresponding to it
 */
export const fromFormatToDate = (
	date: Date | string,
	format: INPUT_DATE_SELECT_FORMAT,
	yearOptions: InputSelectOption[]
): Date => {
	if (format === INPUT_DATE_SELECT_FORMAT.DATE) return date as Date;
	if (format === INPUT_DATE_SELECT_FORMAT.ISO_STRING) {
		const [year, month, day] = (date as string).split('-');
		const result = new Date(Number(year), Number(month) - 1, Number(day));
		result.setFullYear(Number(year));
		result.setMonth(Number(month) - 1);
		result.setDate(Number(day));
		return result;
	}
	if (typeof date === 'string') {
		const yearLastDigits = date.substring(2, 4);
		const year = yearOptions.find((option) => option.label.slice(-2) === yearLastDigits)?.value as number;
		const month = Number(date.substring(0, 2)) - 1;
		const result = new Date(year, month, 1);
		result.setFullYear(year);
		result.setMonth(month);
		result.setDate(1);
		return result;
	}
	return date;
};
